import React, { useEffect } from 'react';
import { Modal } from './UI';
import SafeHtml from './SafeHtml';
import { fmtTimeRange } from '../utils/format';
import { rsvpMeta, formatRespondedAt, interviewerRoster } from '../utils/rsvpStatus';
import RecordingButton, { roundHasEnded } from './RecordingButton';

// ─────────────────────────────────────────────────────────────────────────────
// InterviewRoundModal — everything known about ONE interview round.
//
// The card rows are deliberately terse; this is where the full picture lives:
// the exact slot, whether the invite actually went out, who has answered and
// when, the meeting link, the notes, and who scheduled it.
//
// Read-only by design. Editing stays in the Update Status flow so there is one
// place that writes a round, not two.
// ─────────────────────────────────────────────────────────────────────────────

const RESULT_STYLE = {
  Pass:    { bg: 'rgba(16,185,129,0.12)', color: '#10b981', border: 'rgba(16,185,129,0.35)' },
  Fail:    { bg: 'rgba(239,68,68,0.12)',  color: '#ef4444', border: 'rgba(239,68,68,0.35)'  },
  Pending: { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b', border: 'rgba(245,158,11,0.35)' },
};

const MODE_ICON = { Online: '💻', 'In-Person': '🏢', Phone: '📞' };

const fmtLongDate = (d) => {
  if (!d) return null;
  const dt = new Date(`${d}T00:00:00`);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
};

const fmtStamp = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
};

/**
 * True when an RTE value has no visible content.
 * The editor stores "<p></p>" / "<p><br></p>" for an untouched field, which is
 * a truthy string — rendering it produced a "Result notes" heading with nothing
 * underneath.
 */
const isBlankHtml = (html) =>
  !html || !String(html).replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();

/** Minutes between "18:00" and "18:30". Null when either side is missing. */
const durationMins = (start, end) => {
  const toM = (t) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(String(t || ''));
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
  };
  const a = toM(start), b = toM(end);
  if (a == null || b == null) return null;
  const diff = b - a;
  return diff > 0 ? diff : null;
};

// ── Small presentational pieces ──────────────────────────────────────────────

const Section = ({ title, children }) => (
  <section style={{ marginTop: 22 }}>
    <h3 style={{
      margin: '0 0 10px', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
      textTransform: 'uppercase', color: 'var(--text-muted)',
    }}>
      {title}
    </h3>
    {children}
  </section>
);

/** Label/value pairs on one aligned grid, so values line up down the column. */
const Rows = ({ children }) => (
  <div style={{
    display: 'grid', gridTemplateColumns: 'max-content minmax(0, 1fr)',
    columnGap: 16, rowGap: 8, fontSize: 13, alignItems: 'start',
  }}>
    {children}
  </div>
);

const Row = ({ label, children }) => (
  <>
    <span style={{ color: 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap', paddingTop: 1 }}>{label}</span>
    <span style={{ color: 'var(--text-primary)', minWidth: 0, overflowWrap: 'anywhere' }}>{children}</span>
  </>
);

const Empty = () => <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Not set</span>;

export default function InterviewRoundModal({ isOpen, onClose, round, context = {} }) {
  // Escape closes. Added here rather than in the shared Modal because this
  // dialog is read-only — wiring it globally could discard a half-filled form
  // in one of the editing modals.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen || !round) return null;

  const result = round.result || 'Pending';
  const rs     = RESULT_STYLE[result] || RESULT_STYLE.Pending;
  const roster = interviewerRoster(round);
  const cand   = rsvpMeta(round.candidateResponse);
  const mins   = durationMins(round.time, round.endTime);
  const history = Array.isArray(round.rescheduleHistory) ? round.rescheduleHistory : [];
  const title  = `Round ${round.round || 1}${round.title ? ` · ${round.title}` : ''}`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="lg">
      {/* Status strip: the two things you look for first. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
        <span style={{
          fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 999,
          background: rs.bg, color: rs.color, border: `1px solid ${rs.border}`,
        }}>
          {result}
        </span>
        {round.inviteSentAt ? (
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            <span style={{ color: '#10b981' }}>✓</span> Invite sent {fmtStamp(round.inviteSentAt)}
          </span>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>○ Invite not sent</span>
        )}
      </div>

      {(context.candidateName || context.clientName || context.requirementTitle) && (
        <Section title="Context">
          <Rows>
            {context.candidateName && (
              <Row label="Candidate">
                {context.candidateName}
                {context.candidateEmail && (
                  <span style={{ color: 'var(--text-muted)' }}> · {context.candidateEmail}</span>
                )}
              </Row>
            )}
            {context.clientName && <Row label="Client">{context.clientName}</Row>}
            {context.requirementTitle && <Row label="Requirement">{context.requirementTitle}</Row>}
          </Rows>
        </Section>
      )}

      <Section title="Schedule">
        <Rows>
          <Row label="Date">{fmtLongDate(round.date) || <Empty />}</Row>
          <Row label="Time">
            {round.time ? fmtTimeRange(round.time, round.endTime) : <Empty />}
            {mins != null && <span style={{ color: 'var(--text-muted)' }}> · {mins} min</span>}
          </Row>
          <Row label="Mode">
            {round.mode ? <>{MODE_ICON[round.mode] || ''} {round.mode}</> : <Empty />}
          </Row>
          <Row label="Meeting">
            {round.meetingLink ? (
              <a href={round.meetingLink} target="_blank" rel="noopener noreferrer"
                style={{ color: '#6366f1', fontWeight: 600, textDecoration: 'none' }}>
                💻 Join Teams meeting ↗
              </a>
            ) : (
              <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No meeting link</span>
            )}
          </Row>
          {round.meetingLink && context?.submissionId && (
            <Row label="Recording">
              {roundHasEnded(round)
                ? <RecordingButton submissionId={context.submissionId} round={round} stopPropagation={false} />
                : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Available after the interview ends</span>}
            </Row>
          )}
        </Rows>
      </Section>

      <Section title="Responses">
        <Rows>
          <Row label="Candidate">
            {cand ? (
              <span style={{ color: cand.color, fontWeight: 600 }}>
                {cand.icon} {cand.label}
                {round.candidateRespondedAt && (
                  <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                    {' '}· {formatRespondedAt(round.candidateRespondedAt)}
                  </span>
                )}
              </span>
            ) : round.inviteSentAt ? (
              <span style={{ color: 'var(--text-muted)' }}>
                Awaiting reply
                <span style={{ display: 'block', fontSize: 11, marginTop: 2 }}>
                  Many mail clients — the Gmail app especially — never show an RSVP
                  button, so no reply does not mean they are not coming.
                </span>
              </span>
            ) : (
              <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No invite sent yet</span>
            )}
          </Row>
        </Rows>

        {roster.length > 0 && (
          <div style={{ marginTop: 12, overflowX: 'auto' }} className="custom-scroll">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 360 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}>
                  <th style={{ fontWeight: 500, fontSize: 11, padding: '0 12px 6px 0' }}>Interviewer</th>
                  <th style={{ fontWeight: 500, fontSize: 11, padding: '0 12px 6px 0' }}>Response</th>
                  <th style={{ fontWeight: 500, fontSize: 11, padding: '0 0 6px' }}>Replied</th>
                </tr>
              </thead>
              <tbody>
                {roster.map((iv) => {
                  const im = rsvpMeta(iv.response);
                  return (
                    <tr key={iv.email} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '7px 12px 7px 0', color: 'var(--text-primary)' }}>
                        {iv.name}
                        <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', overflowWrap: 'anywhere' }}>
                          {iv.email}
                        </span>
                      </td>
                      <td style={{ padding: '7px 12px 7px 0', whiteSpace: 'nowrap' }}>
                        {im ? (
                          <span style={{ color: im.color, fontWeight: 600 }}>{im.icon} {im.label}</span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>○ Awaiting</span>
                        )}
                      </td>
                      <td style={{ padding: '7px 0', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {formatRespondedAt(iv.at) || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {history.length > 0 && (
        <Section title="Reschedule history">
          {/* Newest first: "what did it move FROM, and when". Answers the
              question you actually have when a candidate turns up at the old
              time. Each entry is the slot that was vacated. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {[...history].reverse().map((h, i) => (
              <div key={i} style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 8px', fontSize: 12.5, alignItems: 'baseline' }}>
                <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Was</span>
                <span style={{ color: 'var(--text-primary)', textDecoration: 'line-through', whiteSpace: 'nowrap' }}>
                  {fmtLongDate(h.date) || 'no date'}
                  {h.time ? ` · ${fmtTimeRange(h.time, h.endTime)}` : ''}
                </span>
                <span style={{ color: 'var(--text-muted)', fontSize: 11.5, whiteSpace: 'nowrap' }}>
                  moved{h.byName ? ` by ${h.byName}` : ''}{h.at ? ` · ${fmtStamp(h.at)}` : ''}
                </span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {(!isBlankHtml(round.notes) || !isBlankHtml(round.resultDesc)) && (
        <Section title="Notes">
          {!isBlankHtml(round.notes) && (
            <SafeHtml html={round.notes}
              style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.55 }} />
          )}
          {!isBlankHtml(round.resultDesc) && (
            <div style={{ marginTop: isBlankHtml(round.notes) ? 0 : 10 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>Result notes</div>
              <SafeHtml html={round.resultDesc}
                style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.55 }} />
            </div>
          )}
        </Section>
      )}

      {(round.scheduledByName || round.scheduledAt) && (
        <Section title="Audit">
          <Rows>
            {round.scheduledByName && <Row label="Scheduled by">{round.scheduledByName}</Row>}
            {round.scheduledAt && <Row label="Scheduled at">{fmtStamp(round.scheduledAt)}</Row>}
          </Rows>
        </Section>
      )}
    </Modal>
  );
}
